<template>
  <aside
    class="quest-sidebar"
    :class="{
      'quest-sidebar--expanded': isExpanded
    }"
    @click="handleSidebarClick">
    <div class="quest-sidebar__header">
      <Logo
        v-if="isExpanded"
        class="quest-sidebar__logo quest-sidebar__logo--horizontal" />
      <div
        v-else
        class="quest-sidebar__logo-wrapper">
        <MobileLogo
          class="quest-sidebar__logo quest-sidebar__logo--vertical" />
        <OpenIcon
          class="quest-sidebar__expand-icon" />
      </div>
      <button
        v-if="isExpanded"
        class="quest-sidebar__close-btn"
        @click.stop="toggleSidebar">
        <CloseIcon class="quest-sidebar__close-icon" />
      </button>
    </div>

    <nav class="quest-sidebar__nav">
      <ul class="quest-sidebar__list">
        <li
          v-for="(screen, index) in sharedStore.state.screens"
          :key="index"
          class="quest-sidebar__item"
          :class="{
            'quest-sidebar__item--active': sharedStore.state.currentScreenIndex === index,
            'quest-sidebar__item--disabled': !screen.accessible
          }">
          <button
            class="quest-sidebar__button"
            :disabled="!screen.accessible"
            @click.stop="selectScreen(index)">
            <span class="quest-sidebar__chapter-number">
              {{ index + 1 }}
            </span>
            <span
              v-if="isExpanded"
              class="quest-sidebar__chapter-name"
              v-html="$options.filters.md(screen.displayName)" />

            <CheckIcon
              v-if="screen.finished"
              class="quest-sidebar__chapter-icon quest-sidebar__chapter-icon--check" />
            <PencilIcon
              v-else-if="screen.accessible && !screen.finished"
              class="quest-sidebar__chapter-icon quest-sidebar__chapter-icon--pencil" />
            <LockIcon
              v-else-if="!screen.accessible"
              class="quest-sidebar__chapter-icon quest-sidebar__chapter-icon--lock" />
          </button>
        </li>
      </ul>
    </nav>

    <div class="quest-sidebar__footer">
      <button
        class="quest-sidebar__fullscreen-button"
        @click.stop="handleToggleFullscreen">
        <ShrinkIcon
          v-if="fullScreen"
          class="quest-sidebar__fullscreen-icon" />
        <ExpandIcon
          v-else
          class="quest-sidebar__fullscreen-icon" />
      </button>
    </div>
  </aside>
</template>

<script>
import Logo from "@images/svgs/header_logo.svg";
import MobileLogo from "@images/svgs/mobile_header_logo.svg";
import ExpandIcon from "@images/font_awesome/expand-arrows-alt-light.svg";
import ShrinkIcon from "@images/font_awesome/compress-arrows-alt-light.svg";
import CheckIcon from "@images/font_awesome/check-light.svg";
import PencilIcon from "@images/font_awesome/pencil-light.svg";
import LockIcon from "@images/font_awesome/lock-alt-light.svg";
import CloseIcon from "@images/font_awesome/sidebar-close-light.svg";
import OpenIcon from "@images/font_awesome/sidebar-open-light.svg";
import sharedStore from "components/quest/shared-store";

export default {
  name: "QuestSidebar",
  components: {
    Logo,
    MobileLogo,
    ExpandIcon,
    ShrinkIcon,
    CheckIcon,
    PencilIcon,
    LockIcon,
    CloseIcon,
    OpenIcon
  },
  data() {
    return {
      sharedStore,
      isExpanded: false
    }
  },
  props: {
    screens: {
      type: Array,
      required: true
    },
    currentScreenIndex: {
      type: Number,
      required: true
    },
    fullScreen: {
      type: Boolean,
      default: false
    }
  },
  methods: {
    handleSidebarClick(event) {
      // Toggle sidebar when clicking anywhere except interactive elements
      this.toggleSidebar();
    },
    toggleSidebar() {
      this.isExpanded = !this.isExpanded;
      this.$emit('sidebar-toggled', this.isExpanded);
    },
    selectScreen(screenIndex) {
      const screen = this.sharedStore.state.screens[screenIndex];

      if (screen.accessible) {
        this.$emit('screen-selected', screenIndex);
      }
    },
    handleToggleFullscreen() {
      this.$emit('toggle-fullscreen');
    }
  }
}
</script>

<style lang="scss">
.quest-sidebar {
  $root: &;

  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  width: 82px;
  height: 100vh;
  padding: $px16 $px4;
  overflow-y: auto;

  border-right: $px1 solid $grey-9;

  background-color: $white;

  cursor: e-resize;
  transition: width 0.3s ease-in-out, padding 0.3s ease-in-out;

  &::-webkit-scrollbar {
    width: $px8;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: $px4;

    background-color: $grey-8;

    transition: background-color 0.2s ease-in-out;
  }

  &::-webkit-scrollbar-thumb:hover {
    background-color: $grey-6;
  }

  scrollbar-width: thin;
  scrollbar-color: $grey-8 transparent;

  @include breakpoint($xs) {
    display: none;
  }

  &--expanded {
    width: $px240;
    padding: $px16 $px8;

    cursor: w-resize;

    #{$root}__chapter-number {
      margin-right: $px8;
    }
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: $px32;

    #{$root}--expanded & {
      justify-content: space-between;
    }
  }

  &__logo-wrapper {
    position: relative;

    cursor: e-resize;

    &:hover {
      #{$root}__logo--vertical {
        opacity: 0.15;
      }

      #{$root}__expand-icon {
        opacity: 1;
      }
    }
  }

  &__logo {

    &--horizontal {
      width: auto;
      max-width: $px192;

      cursor: w-resize;
    }

    &--vertical {
      width: $px48;
      height: auto;
      margin-top: -$px1; // SVG icon has 2px inner margin

      cursor: e-resize;

      transition: opacity 0.2s ease-in-out;
    }
  }

  &__expand-icon {
    position: absolute;
    top: 50%;
    left: 50%;

    transform: translate(-50%, -50%);

    width: $px24;
    height: $px24;

    color: $grey-4;

    opacity: 0;
    pointer-events: none;
    transition: opacity 0.2s ease-in-out;
  }

  &__close-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: $px4;

    border: none;
    border-radius: $px4;

    background: none;

    cursor: pointer;
    transition: background-color 0.2s ease-in-out;

    &:hover {
      background-color: $grey-10;

      cursor: pointer;
    }
  }

  &__close-icon {
    width: $px24;
    height: $px24;

    color: $grey-6;

    transition: color 0.2s ease-in-out;

    &:hover {
      color: $grey-4;
    }
  }

  &__nav {
    flex: 1;
  }

  &__list {
    margin: 0;
    padding: 0;

    list-style: none;
  }

  &__item {
    margin-bottom: $px8;

    &--active {
      #{$root}__button {
        background-color: $blue-10;

        color: $blue-1;
      }
    }

    &--disabled {
      #{$root}__button {
        opacity: 0.4;
        cursor: not-allowed;
      }
    }
  }

  &__button {
    display: flex;
    align-items: center;
    width: 100%;
    padding: $px8;

    border: none;
    border-radius: $px4;

    background-color: $grey-10;

    color: $grey-1;
    font-size: 14px;
    font-weight: 500;
    text-align: left;

    cursor: pointer;
    transition: all 0.2s ease-in-out;

    &:hover:not(:disabled) {
      background-color: $grey-9;

      transform: translateX($px2);
    }

    &:not(:disabled) {
      cursor: pointer;
    }
  }

  &__chapter-number {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: $px24;
    height: $px24;

    border-radius: $px4;

    background-color: rgba(0, 0, 0, 0.1);

    font-size: 12px;
    font-weight: 700;
  }

  &__chapter-name {
    flex: 1;
    overflow: hidden;

    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__chapter-icon {
    width: $px16;
    height: $px16;
    margin-left: $px8;
    flex-shrink: 0;

    &--check {
      color: $green-5;
    }

    &--pencil {
      color: $blue-5;
    }

    &--lock {
      color: $grey-7;
    }
  }

  &__footer {
    display: flex;
    justify-content: center;
    padding-top: $px16;
  }

  &__fullscreen-button {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: $px8;

    border: none;
    border-radius: $px4;

    background: none;

    cursor: pointer;
    transition: background-color 0.2s ease-in-out;

    &:hover {
      background-color: $grey-10;

      cursor: pointer;
    }
  }

  &__fullscreen-icon {
    width: $px24;
    height: $px24;

    color: $grey-8;

    transition: all 0.2s ease-in-out;

    &:hover {
      color: $grey-6;

      transform: scale(1.2);
    }
  }
}
</style>
